/**
 * EntrepriseInboxRow — une pièce reçue du portail (Files PMS › Entreprise) — Batch #14.314.
 */
import { useState } from "react";
import axios from "axios";
import { Check, Loader2, Trash2 } from "lucide-react";
import { toast } from "@/lib/toast";
import { fmtCAD } from "@/lib/format";
import { ReceiptThumb } from "../entrepreneurs/_shared";
import { API, authH } from "./_shared";
import { ENT_KINDS } from "./_entreprise";

const API_KIND = { revenues: "revenues", expenses: "expenses", mileage: "mileage" };

export default function EntrepriseInboxRow({ kind, item, onChanged }) {
  const cfg = ENT_KINDS[kind];
  const [busy, setBusy] = useState(null);
  const pending = item.reviewed === false;
  const dateStr = item.date || item.trip_date;

  const review = async () => {
    setBusy("review");
    try {
      await axios.post(`${API}/admin/entrepreneurs/${item.master_id}/entries/${API_KIND[kind]}/${item.id}/review`, {}, { headers: authH() });
      toast.success("Pièce marquée traitée");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(null); }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer cette pièce reçue du portail ? Le client ne la verra plus dans son portail.")) return;
    setBusy("delete");
    try {
      await axios.delete(`${API}/admin/entrepreneurs/${item.master_id}/${API_KIND[kind]}/${item.id}`, { headers: authH() });
      toast.success("Pièce supprimée");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(null); }
  };

  return (
    <div className={`flex items-center gap-3 p-3 ${pending ? "" : "opacity-70"}`} data-testid={`ent-inbox-row-${item.id}`}>
      {kind === "expenses" && item.receipt_url ? <ReceiptThumb url={item.receipt_url} className="w-11 h-11" />
        : <div className={`w-11 h-11 rounded-lg ${cfg.headBg} border border-slate-100 flex items-center justify-center flex-shrink-0`}><cfg.icon className={`w-4 h-4 ${cfg.text}`} /></div>}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-slate-800 text-sm truncate">{cfg.primary(item)}</span>
          <span className="text-[11px] text-slate-500 tabular-nums">· {dateStr}</span>
          {pending ? <span className="text-[10px] font-bold text-rose-600 inline-flex items-center gap-1" data-testid={`ent-inbox-new-${item.id}`}><span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" /> À traiter</span>
            : <span className="text-[10px] font-semibold text-emerald-700 inline-flex items-center gap-1"><Check className="w-3 h-3" /> Traitée{item.reviewed_at ? ` · ${String(item.reviewed_at).slice(0, 10)}` : ""}</span>}
        </div>
        <div className="text-[11px] text-slate-500 truncate mt-0.5">
          {cfg.secondary(item)}
          {kind !== "mileage" && (item.tps > 0 || item.tvq > 0) && <span className="ml-1 text-slate-400">· TPS {fmtCAD(item.tps)} · TVQ {fmtCAD(item.tvq)}</span>}
          {kind !== "mileage" && item.tax_status === "unknown" && <span className="ml-1 text-amber-700">· taxes non précisées par le client</span>}
        </div>
        {item.notes && <div className="text-[10px] text-slate-400 mt-0.5 truncate">{item.notes}</div>}
      </div>
      <span className="font-mono text-sm font-semibold text-slate-800 whitespace-nowrap" data-testid={`ent-inbox-amount-${item.id}`}>{kind === "mileage" ? `${item.km_distance} km` : fmtCAD(item.total)}</span>
      {pending && (
        <button onClick={review} disabled={!!busy} className="px-2 py-1 rounded text-xs bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1 whitespace-nowrap" data-testid={`ent-inbox-review-${item.id}`}>
          {busy === "review" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Marquer traité
        </button>
      )}
      <button onClick={remove} disabled={!!busy} className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 p-1.5 rounded disabled:opacity-50" title="Supprimer" data-testid={`ent-inbox-delete-${item.id}`}>
        {busy === "delete" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}
